import React, { useState, useRef } from 'react';
import { Volume2, VolumeX, Trash2, GripHorizontal, ChevronRight, ChevronLeft } from 'lucide-react';
import { AudioTrackLayer, SelectedMediaTarget } from './types';

interface AudioTrackLaneProps {
  layer: AudioTrackLayer;
  trackIndex: number;
  totalSequenceDuration: number;
  selectedMedia: SelectedMediaTarget;
  onSelect: (id: string) => void;
  onVolumeChange: (id: string, volume: number) => void;
  onToggleMute: (id: string) => void;
  onOffsetChange: (id: string, newOffset: number) => void;
  onTrimStartChange: (id: string, trimStart: number) => void;
  onRemove: (id: string) => void;
  formatTime: (secs: number) => string;
}

export function AudioTrackLane({
  layer,
  trackIndex,
  totalSequenceDuration,
  selectedMedia,
  onSelect,
  onVolumeChange,
  onToggleMute,
  onOffsetChange,
  onTrimStartChange,
  onRemove,
  formatTime,
}: AudioTrackLaneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef<number>(0);
  const initialOffsetRef = useRef<number>(0);
  const laneRef = useRef<HTMLDivElement>(null);

  const isSelected = selectedMedia?.type === 'audio' && selectedMedia.id === layer.id;

  // Effective playable length = source duration minus trim start
  const effectiveDuration = Math.max(0.1, layer.duration - layer.trimStart);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(layer.id);
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
    initialOffsetRef.current = layer.startOffset;

    const handleWindowMouseMove = (moveEvent: MouseEvent) => {
      if (!laneRef.current || totalSequenceDuration <= 0) return;
      const laneRect = laneRef.current.getBoundingClientRect();
      const deltaPixels = moveEvent.clientX - dragStartXRef.current;
      const deltaSeconds = (deltaPixels / laneRect.width) * totalSequenceDuration;
      const newOffset = Math.max(0, Math.min(totalSequenceDuration, initialOffsetRef.current + deltaSeconds));
      onOffsetChange(layer.id, Math.round(newOffset * 10) / 10);
    };

    const handleWindowMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
  };

  const leftPct = totalSequenceDuration > 0 ? (layer.startOffset / totalSequenceDuration) * 100 : 0;
  const widthPct = totalSequenceDuration > 0 ? (effectiveDuration / totalSequenceDuration) * 100 : 100;

  return (
    <div className="bg-white/90 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-800 p-2 min-h-[64px] flex items-start gap-2 relative overflow-hidden">
      {/* Header Controls */}
      <div className="w-48 flex-shrink-0 flex flex-col gap-1.5 bg-slate-100 dark:bg-slate-950 px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onToggleMute(layer.id)}
            className={`p-1 rounded transition-colors ${
              layer.muted ? 'bg-red-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-purple-600 dark:text-purple-400'
            }`}
            title={layer.muted ? 'Unmute' : 'Mute'}
          >
            {layer.muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
          </button>
          <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 truncate flex-1">Trk{trackIndex}</span>
          <button
            type="button"
            onClick={() => onRemove(layer.id)}
            className="text-red-500 hover:text-red-600 p-0.5"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>

        {/* Volume slider */}
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-slate-500 dark:text-slate-400 w-6">Vol</span>
          <input
            type="range" min="0" max="200" value={layer.volume}
            onChange={(e) => onVolumeChange(layer.id, Number(e.target.value))}
            className="flex-1 accent-purple-500 cursor-pointer h-1"
          />
          <span className="text-[9px] font-mono text-purple-600 dark:text-purple-400 w-7 text-right">{layer.volume}%</span>
        </div>

        {/* Trim Start control */}
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-slate-500 dark:text-slate-400 w-6">Src</span>
          <input
            type="range"
            min="0"
            max={Math.max(0, layer.duration - 0.5)}
            step="0.1"
            value={layer.trimStart}
            onChange={(e) => onTrimStartChange(layer.id, Number(e.target.value))}
            className="flex-1 accent-yellow-500 cursor-pointer h-1"
          />
          <span className="text-[9px] font-mono text-yellow-600 dark:text-yellow-400 w-10 text-right">{formatTime(layer.trimStart)}</span>
        </div>
        <div className="text-[9px] text-slate-500">
          Plays {formatTime(layer.trimStart)} → {formatTime(Math.min(layer.duration, layer.trimStart + effectiveDuration))}
        </div>
      </div>

      {/* Interactive Drag-to-Move Track Lane */}
      <div ref={laneRef} className="flex-1 relative h-12 bg-slate-100/60 dark:bg-slate-950/40 rounded-lg overflow-hidden self-center">
        <div
          onMouseDown={handleMouseDown}
          style={{
            left: `${Math.max(0, Math.min(95, leftPct))}%`,
            width: `${Math.max(2, Math.min(100 - leftPct, widthPct))}%`,
          }}
          className={`absolute top-0 bottom-0 rounded-lg border p-1.5 text-white flex items-center justify-between text-[10px] font-bold shadow cursor-grab active:cursor-grabbing transition-shadow ${
            layer.muted
              ? 'bg-slate-800/80 border-slate-700 text-slate-500'
              : 'bg-gradient-to-r from-purple-800 to-indigo-800 border-purple-500 hover:brightness-110'
          } ${
            isSelected ? 'ring-2 ring-cyan-400 border-cyan-400 shadow-cyan-500/50' : ''
          } ${isDragging ? 'opacity-80' : ''}`}
        >
          <div className="flex items-center gap-1 truncate min-w-0">
            <GripHorizontal className="w-3 h-3 text-purple-300 flex-shrink-0" />
            <span className="truncate text-[9px]">{layer.name}</span>
          </div>
          <span className="text-[9px] font-mono opacity-75 flex-shrink-0 ml-1">
            {formatTime(effectiveDuration)}
          </span>
        </div>
      </div>
    </div>
  );
}
